import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, useLocation, useParams, useSearchParams, Navigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import './App.css'
import { useTranslation } from "react-i18next";
import DOMPurify from 'dompurify';
import { app, analytics } from '.';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';
import { getAuth, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, updateDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import FineTuning from './components/FineTuning';
import Evals from './components/Evals';
import Offres from './components/Offres';
import ClassementEntreprises from './components/ClassementEntreprises';
import PrixMalbouffe from './components/PrixMalbouffe';
import ProjectionsFrance from './components/ProjectionsFrance';
import Toaster from './components/Toaster';
import { HubspotChatRefresher } from './HubspotChatRefresher';
import { ResultDisplay } from 'nutriscore-shared-ui';
import i18n from './i18n';

/** Retourne "fr" si la langue du navigateur est le français, "en" sinon */
function getLocaleFromBrowser() {
  const lang = i18n.language || navigator.language || navigator.userLanguage || 'en';
  return String(lang).toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

/** Hook pour obtenir le préfixe de langue et les chemins préfixés */
function useLang() {
  const { lang } = useParams();
  const currentLang = lang || getLocaleFromBrowser();
  const path = (p) => (p === '/' ? `/${currentLang}` : `/${currentLang}${p}`);
  return { lang: currentLang, path };
}

const SUPPORTED_LANGS = ['fr', 'en'];

/** Redirection de / vers /{lang} en préservant les paramètres de partage (?url=...) */
function RootRedirect() {
  const location = useLocation();
  return <Navigate to={`/${getLocaleFromBrowser()}${location.search || ''}`} replace />;
}

/** Layout principal avec préfixe de langue */
function LangLayout({ isDevelopment, isAuthenticated, authLoading, email, setEmail, handleSendSignInLink, handleSignOut, toaster, closeToaster, t }) {
  const { lang, path } = useLang();
  const location = useLocation();
  const betaBannerRef = useRef(null);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  useEffect(() => {
    const el = betaBannerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--beta-banner-height', `${el.offsetHeight}px`);
    });
    ro.observe(el);
    document.documentElement.style.setProperty('--beta-banner-height', `${el.offsetHeight}px`);
    return () => ro.disconnect();
  }, [lang]);

  if (lang && !SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/${getLocaleFromBrowser()}`} replace />;
  }

  const isClassement = location.pathname.includes('classement-entreprises');
  const isPrixMalbouffe = location.pathname.includes('prix-malbouffe');
  const metaTitle = isPrixMalbouffe ? t('MetaTitlePrixMalbouffe') : isClassement ? t('MetaTitleClassement') : t('MetaTitleHome');
  const metaDescription = isPrixMalbouffe ? t('MetaDescriptionPrixMalbouffe') : isClassement ? t('MetaDescriptionClassement') : t('MetaDescriptionHome');
  const canonicalUrl = `https://job-nutriscore.eu/${lang || 'fr'}${isPrixMalbouffe ? '/prix-malbouffe' : isClassement ? '/classement-entreprises' : ''}`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Job-Nutriscore Product Manager" />
        <meta property="og:image" content="https://job-nutriscore.eu/images/logo-nutriscore.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:locale" content={lang === 'en' ? 'en_US' : 'fr_FR'} />
        <meta property="og:locale:alternate" content={lang === 'en' ? 'fr_FR' : 'en_US'} />
        <meta name="author" content="Jacques Giraudel" />
        <meta property="article:modified_time" content="2026-05-04T12:00:00+02:00" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://job-nutriscore.eu/images/logo-nutriscore.png" />
      </Helmet>
      <HubspotChatRefresher />
      <div ref={betaBannerRef} className="beta-banner" role="banner">
        <span className="beta-banner-badge">BETA</span>
        <span className="beta-banner-text">{t('BetaBannerText')}</span>
      </div>
      <div className="app-layout">
        <div className="page-container">
          <header className="app-header">
            <Link to={path('/')} className="logo-title-link" aria-label={t('NutriScoreTitle')}>
              <img src="/images/logo-nutriscore.png" alt="" className="nutri-score-logo" />
              <h1 className="app-title">{t('NutriScoreTitle')}</h1>
            </Link>
            <div className="header-links">
              <Link to={path('/classement-entreprises')} className="classement-link">{t('ClassementLink')}</Link>
              <Link to={path('/prix-malbouffe')} className="classement-link prix-malbouffe-link">{t('PrixMalbouffeLink')}</Link>
              {isDevelopment && (
                <Link to={path('/projections-france')} className="classement-link">{t('NavigationProjectionsFrance')}</Link>
              )}
            </div>
          </header>
          {isDevelopment && (
            <div className="auth-container">
              {authLoading ? (
                <div className="auth-loading">
                  <span>{t('AuthenticationVerification')}</span>
                </div>
              ) : !isAuthenticated ? (
                <div className="auth-form">
                  <input
                    type="email"
                    placeholder={t('AuthEmailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-email-input"
                  />
                  <button
                    onClick={handleSendSignInLink}
                    disabled={authLoading}
                    className="auth-button"
                  >
                    {authLoading ? t('AuthSending') : t('AuthConnectButton')}
                  </button>
                </div>
              ) : (
                <div className="auth-status">
                  <span className="auth-email">{email}</span>
                  <button onClick={handleSignOut} className="auth-button auth-signout">
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          )}
          <Navigation isDevelopment={isDevelopment} />
          <Outlet />
        </div>
        <footer className="app-footer">
          <a href={path('/privacy-policy.html')} className="footer-link">{t('PrivacyPolicyLink')}</a>
          <span className="footer-sep"> · </span>
          <a href={path('/mentions-legales.html')} className="footer-link">{t('LegalNoticeLink')}</a>
          <span className="footer-sep"> · </span>
          <a href={path('/terms-of-service.html')} className="footer-link">{t('TermsOfServiceLink')}</a>
          <span className="footer-sep"> · </span>
          <a href={path('/faq.html')} className="footer-link">{t('FaqLink')}</a>
          <span className="footer-sep"> · </span>
          <a href={path('/about.html')} className="footer-link">{t('AboutLink')}</a>
          <span className="footer-sep"> · </span>
          <a href="https://chromewebstore.google.com/detail/ejljdkaeaodiklbgeliannenccciignl" target="_blank" rel="noopener noreferrer" className="footer-link">{t('ExtensionChromeLink')}</a>
        </footer>
      </div>
      <Toaster message={toaster.message} type={toaster.type} isVisible={toaster.isVisible} onClose={closeToaster} />
    </>
  );
}

const SHARE_DB = 'nutriscore-share';
const SHARE_STORE = 'pending';

function openShareDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(SHARE_DB, 1);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

function getShareData(shareId) {
  return openShareDB().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(SHARE_STORE, 'readwrite');
    const req = tx.objectStore(SHARE_STORE).get(shareId);
    req.onsuccess = () => {
      const data = req.result;
      if (data) {
        tx.objectStore(SHARE_STORE).delete(shareId);
        resolve({ url: data.url || '', text: data.text || '', title: data.title || '' });
      } else {
        resolve(null);
      }
    };
    req.onerror = () => reject(req.error);
  }));
}

/** Extrait une URL depuis url, text ou title (sur Android, l'URL est souvent dans text) */
function extractSharedUrlFromData({ url, text, title }) {
  const u = String(url || '').trim();
  if (u && /^https?:\/\//i.test(u)) return u;
  const t = String(text || '').trim();
  if (t) {
    if (/^https?:\/\/\S*$/i.test(t)) return t;
    const match = t.match(/https?:\/\/[^\s\u00A0]+/iu);
    if (match) return match[0];
  }
  const ti = String(title || '').trim();
  if (ti && /^https?:\/\//i.test(ti)) return ti;
  return null;
}

function extractSharedUrl(searchParams) {
  return extractSharedUrlFromData({
    url: searchParams.get('url'),
    text: searchParams.get('text'),
    title: searchParams.get('title')
  });
}

function NutriScoreLoader() {
  const [index, setIndex] = useState(0);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const levelsOrder = [0, 1, 2, 3, 4];
    const letterInterval = setInterval(() => {
      setIndex((prev) => {
        const currentPos = levelsOrder.indexOf(prev);
        const nextPos = (currentPos + 1) % levelsOrder.length;
        return levelsOrder[nextPos];
      });
    }, 1000);
    const rotationInterval = setInterval(() => {
      setRotation((prev) => (prev + 4) % 360);
    }, 40);
    return () => {
      clearInterval(letterInterval);
      clearInterval(rotationInterval);
    };
  }, []);

  const levels = [
    { color: '#058040', letter: 'A' },
    { color: '#84bc2d', letter: 'B' },
    { color: '#fdc705', letter: 'C' },
    { color: '#ef7c00', letter: 'D' },
    { color: '#e03d0b', letter: 'E' },
  ];

  const current = levels[index] || levels[0];
  const baseAngles = [-126, -54, 18, 90, 162];

  return (
    <div className="loader-inline" style={{ background: 'transparent', boxShadow: 'none' }}>
      <svg
        width="80"
        height="80"
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#038141"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[0] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#85bb2f"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[1] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#fecb01"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[2] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#ee8005"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[3] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#e63e0f"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[4] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="49"
          fill="#ffffff"
        />
        <circle cx="60" cy="60" r="48" fill={current.color} />
        <text
          x="60"
          y="62"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          fontSize="40"
          fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          fontWeight="700"
        >
          {current.letter}
        </text>
      </svg>
    </div>
  );
}

// Composant pour la page d'accueil (analyse des offres d'emploi)
function HomePage({ isAuthenticated, isDevelopment }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offreEmploi, setOffreEmploi] = useState('');
  const [score, setScore] = useState(null);
  const [organization, setOrganization] = useState('');
  const [explanation, setExplanation] = useState('');
  const [references, setReferences] = useState('');
  const [position, setPosition] = useState('');
  const [company, setCompany] = useState('');
  const [debugText, setDebugText] = useState(false);
  const [improve, setImprove] = useState(false);
  const [question, setQuestion] = useState(false);
  const [scoreDetail, setScoreDetail] = useState(false);
  const [debugBusinessInitiative, setDebugBusinessInitiative] = useState(false);
  const [debugBusinessInitiativeSpecialQuestion, setDebugBusinessInitiativeSpecialQuestion] = useState(false);
  const [debugExperimentalEnvironment, setDebugExperimentalEnvironment] = useState(false);
  const [debugExperimentalEnvironmentSpecialQuestion, setDebugExperimentalEnvironmentSpecialQuestion] = useState(false);
  const [criteria, setCriteria] = useState(null);
  const [debugBusinessInitiativeInput, setDebugBusinessInitiativeInput] = useState('');
  const [debugExperimentalEnvironmentInput, setDebugExperimentalEnvironmentInput] = useState('');
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [currentLoadingScore, setCurrentLoadingScore] = useState(5);
  const [loadingAnimationRef, setLoadingAnimationRef] = useState(null);
  const [showReferences, setShowReferences] = useState(false);
  const [showResponsibleApplication, setShowResponsibleApplication] = useState(false);
  const [shouldBlinkResponsibleApplication, setShouldBlinkResponsibleApplication] = useState(false);
  const [showOurBigTechs, setShowOurBigTechs] = useState(false);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const [calculationDetails, setCalculationDetails] = useState(null);
  const [notAProductJobOffer, setNotAProductJobOffer] = useState(false);
  const [inputMode, setInputMode] = useState('url');
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [addToDatasetsDataSet, setAddToDatasetsDataSet] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1-2025-04-14');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [thumbsUpAnimated, setThumbsUpAnimated] = useState(false);
  const [thumbsDownAnimated, setThumbsDownAnimated] = useState(false);
  const [thumbsUpMessage, setThumbsUpMessage] = useState(null);
  const [thumbsDownMessage, setThumbsDownMessage] = useState(null);
  const [isVotingJunkFoodPrize, setIsVotingJunkFoodPrize] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState('');
  const [availableModels, setAvailableModels] = useState([
    "gpt-4.1-2025-04-14",
    "gpt-4.1-mini-2025-04-14",
    "gpt-4.1-nano-2025-04-14",
  ]);
  const formRef = useRef(null);
  const fromShareRef = useRef(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const debug = isDevelopment;

  useEffect(() => {
    setIsStandalone(
      window.matchMedia?.('(display-mode: standalone)')?.matches || !!window.navigator?.standalone
    );
  }, []);

  // Capturer l'événement d'installation PWA (Chrome, Edge)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    } else {
      setShowInstallInstructions(!showInstallInstructions);
    }
  };

  // Détecter un partage via Web Share Target (PWA) et lancer l'analyse automatiquement
  // GET : url/text/title dans les query params | POST : shareId (données en IndexedDB)
  useEffect(() => {
    const shareId = searchParams.get('shareId');
    if (shareId) {
      fromShareRef.current = true;
      getShareData(shareId).then((data) => {
        if (!data || (!data.url && !data.text && !data.title)) {
          return;
        }
        const sharedUrl = extractSharedUrlFromData(data);
        const sharedText = [data.text, data.title].filter(Boolean).join('\n\n').trim();
        Sentry.addBreadcrumb({ category: 'share', message: 'Partage reçu (shareId)', data: { hasUrl: !!sharedUrl, hasText: !!sharedText } });
        if (sharedUrl) {
          setInputMode('url');
          setUrlInput(sharedUrl);
          setTimeout(() => {
            formRef.current?.requestSubmit();
            setSearchParams({}, { replace: true });
          }, 150);
        } else if (sharedText) {
          setInputMode('text');
          setOffreEmploi(sharedText);
          setTimeout(() => {
            formRef.current?.requestSubmit();
            setSearchParams({}, { replace: true });
          }, 150);
        } else {
          return;
        }
      }).catch((err) => Sentry.captureException(err, { tags: { context: 'share_getShareData' } }));
      return;
    }
    const sharedUrl = extractSharedUrl(searchParams);
    const sharedText = [searchParams.get('text'), searchParams.get('title')].filter(Boolean).join('\n\n').trim();
    if (sharedUrl) {
      Sentry.addBreadcrumb({ category: 'share', message: 'Partage via URL (GET)', data: { url: sharedUrl.substring(0, 80) } });
      fromShareRef.current = true;
      setInputMode('url');
      setUrlInput(sharedUrl);
      setTimeout(() => {
        formRef.current?.requestSubmit();
        setSearchParams({}, { replace: true });
      }, 150);
      return;
    }
    if (sharedText) {
      Sentry.addBreadcrumb({ category: 'share', message: 'Partage via texte (GET)', data: { length: sharedText.length } });
      fromShareRef.current = true;
      setInputMode('text');
      setOffreEmploi(sharedText);
      setTimeout(() => {
        formRef.current?.requestSubmit();
        setSearchParams({}, { replace: true });
      }, 150);
      return;
    }
  }, [searchParams, setSearchParams]);

  // Fonction pour formater les détails de score en HTML lisible
  const formatScoreDetail = (scoreDetailData) => {
    if (!scoreDetailData) {
      return `<p>${t('NoDetailsAvailable')}</p>`;
    }
    
    let html = '<div class="score-detail-container">';
    html += `<div class="score-detail-entry">`;
    
    // Business Initiative
    if (scoreDetailData.businessInitiative) {
      html += `<div class="score-detail-section">`;
      html += `<h6>${t('BusinessInitiative')}</h6>`;
      html += `<div class="score-detail-item"><strong>${t('Score')} :</strong> ${scoreDetailData.businessInitiative.score || t('NotDefined')}/5</div>`;
      html += `<div class="score-detail-item"><strong>${t('Appreciation')} :</strong> ${scoreDetailData.businessInitiative.appreciation || t('NotDefined')}</div>`;
      html += `<div class="score-detail-item"><strong>${t('Explanation')} :</strong> ${scoreDetailData.businessInitiative.explanation || t('NotDefined')}</div>`;
      html += `</div>`;
    }
    
    // Experimental Environment
    if (scoreDetailData.experimentalEnvironment) {
      html += `<div class="score-detail-section">`;
      html += `<h6>${t('ExperimentalEnvironment')}</h6>`;
      html += `<div class="score-detail-item"><strong>${t('Score')} :</strong> ${scoreDetailData.experimentalEnvironment.score || t('NotDefined')}/5</div>`;
      html += `<div class="score-detail-item"><strong>${t('Appreciation')} :</strong> ${scoreDetailData.experimentalEnvironment.appreciation || t('NotDefined')}</div>`;
      html += `<div class="score-detail-item"><strong>${t('Explanation')} :</strong> ${scoreDetailData.experimentalEnvironment.explanation || t('NotDefined')}</div>`;
      html += `</div>`;
    }
    
    // Global Evaluation
    if (scoreDetailData.globalEvaluation) {
      html += `<div class="score-detail-section">`;
      html += `<h6>${t('GlobalEvaluation')}</h6>`;
      html += `<div class="score-detail-item"><strong>${t('Evaluation')} :</strong> ${scoreDetailData.globalEvaluation}</div>`;
      html += `</div>`;
    }
    
    // References
    if (scoreDetailData.references) {
      html += `<div class="score-detail-section">`;
      html += `<h6>${t('References')}</h6>`;
      html += `<div class="score-detail-item"><strong>${t('References')} :</strong>`;
      html += `<div class="references-content">${scoreDetailData.references}</div>`;
      html += `</div></div>`;
    }
    
    html += `</div></div>`;
    return html;
  };

  // Récupérer les modèles fine-tunés au chargement du composant (seulement si authentifié)
  useEffect(() => {
    if (isAuthenticated) {
      fetchFineTunedModels();
    }
  }, [isAuthenticated]);

  // Animation de roulette pendant le chargement
  useEffect(() => {
    let interval;
    
    if (isLoading) {
      const scores = [5, 4, 3, 2, 1]; // A, B, C, D, E
      let index = 0;
      let speed = 200; // Vitesse initiale
      
      const animate = () => {
        setCurrentLoadingScore(scores[index]);
        index = (index + 1) % scores.length;
        interval = setTimeout(animate, speed);
      };
      
      // Démarrer l'animation rapide
      animate();
      
      // Stocker la référence pour pouvoir l'arrêter
      setLoadingAnimationRef({ interval });
    }
    
    return () => {
      if (interval) {
        clearTimeout(interval);
      }
    };
  }, [isLoading]);

  // Ralentir et arrêter l'animation quand l'analyse est terminée
  useEffect(() => {
    if (!isLoading && score && loadingAnimationRef) {
      // Arrêter l'animation rapide
      if (loadingAnimationRef.interval) {
        clearTimeout(loadingAnimationRef.interval);
      }
      
      // Commencer le ralentissement progressif
      const scores = [5, 4, 3, 2, 1]; // A, B, C, D, E
      let index = 0;
      let speed = 200;
      
      const slowDown = () => {
        speed = Math.min(speed + 100, 1000); // Augmenter l'intervalle progressivement
        setCurrentLoadingScore(scores[index]);
        index = (index + 1) % scores.length;
        
        if (speed < 1000) {
          setTimeout(slowDown, speed);
        } else {
          // Arrêter sur le score final
          setCurrentLoadingScore(score);
          setShowFinalScore(true);
        }
      };
      
      // Démarrer le ralentissement
      slowDown();
    }
  }, [isLoading, score, loadingAnimationRef]);

  const fetchFineTunedModels = async () => {
    try {
      const functions = getFunctions(app, 'europe-west3');
      const listOrganizationModels = httpsCallable(functions, 'listOrganizationModels');
      const result = await listOrganizationModels();
      
      if (result.data && result.data.models) {
        // Filtrer pour ne garder que les modèles appartenant à l'organisation Nutriscore
        const fineTunedModels = result.data.models
          .filter(model => model.owned_by && model.owned_by.startsWith('user-'))
          .map(model => model.id);
        
        // Combiner avec les modèles de base existants
        const baseModels = [
          "gpt-4.1-2025-04-14",
          "gpt-4.1-mini-2025-04-14",
          "gpt-4.1-nano-2025-04-14",
        ];
        
        // Supprimer les doublons et trier
        const allModels = [...new Set([...baseModels, ...fineTunedModels])].sort();
        setAvailableModels(allModels);
      }
    } catch (error) {
      console.warn('Erreur lors de la récupération des modèles fine-tunés:', error);
      Sentry.captureException(error, { tags: { context: 'fetchFineTunedModels' } });
      // Continue avec les modèles de base si l'appel échoue            
    }
  };

  // Fonction pour sauvegarder l'offre en base de données (identique au backend)
  const saveToFirebase = async (jobOffer, criteria, finalResponse, computedResult, language, model) => {
    const db = getFirestore(app);
    const timestamp = serverTimestamp();

    try {
      // Create jobOffer document
      const jobOfferRef = await addDoc(collection(db, "jobOffersForEvals"), {
        content: jobOffer,
        language: language,
        position: finalResponse.position,
        company: finalResponse.company,
        model: model,
        score: computedResult.score,
        scoreDetail: computedResult.scoreDetail,
        createdAt: timestamp,
        updatedAt: timestamp
      });

      console.log('saveToFirebase: Created jobOffer document with ID:', jobOfferRef.id);

      // Create businessInitiative document
      const businessInitiativeRef = await addDoc(collection(db, "businessInitiatives"), {
        jobOfferId: jobOfferRef.id,
        criteria: criteria.businessInitiative,
        createdAt: timestamp
      });

      // Create experimentalEnvironment document
      const experimentalEnvironmentRef = await addDoc(collection(db, "experimentalEnvironments"), {
        jobOfferId: jobOfferRef.id,
        criteria: criteria.experimentalEnvironment,
        createdAt: timestamp
      });

      // Create explanation document
      const explanationRef = await addDoc(collection(db, "explanations"), {
        jobOfferId: jobOfferRef.id,
        position: finalResponse.position,
        explanation: finalResponse.explanation,
        improve: finalResponse.improve,
        debug: finalResponse.debug,
        references: finalResponse.references,
        company: finalResponse.company,
        score: computedResult.score,
        scoreDetail: computedResult.scoreDetail,
        createdAt: timestamp
      });

      // Update jobOffer with references to all related documents
      await updateDoc(doc(db, "jobOffersForEvals", jobOfferRef.id), {
        businessInitiativeId: businessInitiativeRef.id,
        experimentalEnvironmentId: experimentalEnvironmentRef.id,
        explanationId: explanationRef.id,
        updatedAt: timestamp
      });

      console.log('saveToFirebase: All documents created successfully - jobOfferId:', jobOfferRef.id);
    } catch (error) {
      console.error('saveToFirebase: Error saving to Firebase -', error.message);
      Sentry.captureException(error, { tags: { context: 'saveToFirebase' } });
      throw error;
    }
  };

  // Fonction pour déclencher la sauvegarde avec les bons paramètres
  const handleSaveToFirebase = async () => {
    if (!score || !offreEmploi || !criteria) {
      setSaveError(true);
      return;
    }

    setIsSaving(true);
    setSaveError(false);
    setSaveSuccess(false);

    try {      
      // Utiliser la langue détectée par l'API, sinon fallback sur le calcul côté client
      const languageToUse = detectedLanguage;

      const finalResponse = {
        position: position,
        company: company,
        explanation: explanation,
        improve: improve,
        debug: debugText,
        references: references,        
      };

      const computedResult = {
        score: score,
        scoreDetail: scoreDetail
      };

      // Utiliser les criteria récupérés depuis l'API
      await saveToFirebase(offreEmploi, criteria, finalResponse, computedResult, languageToUse, selectedModel);

      setSaveSuccess(true);
      console.log('Offre sauvegardée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      Sentry.captureException(error, { tags: { context: 'handleSaveToFirebase' } });
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSection = (section, isOpen) => {
    logEvent(analytics, 'result_section_toggle', { section, is_open: isOpen });
  };

  const handleThumbsUp = () => {
    logEvent(analytics, 'thumbs_up', {  }); // Send thumbs_up event
    
    // Déclencher l'animation et le message
    setThumbsUpAnimated(true);
    setThumbsUpMessage('Merci !');
    
    // Arrêter l'animation après 1 seconde
    setTimeout(() => {
      setThumbsUpAnimated(false);
    }, 1000);
    
    // Masquer le message après 3 secondes
    setTimeout(() => {
      setThumbsUpMessage(null);
    }, 3000);
  };

  const handleThumbsDown = async () => {
    logEvent(analytics, 'thumbs_down', {  }); // Send thumbs_down event
    
    // Déclencher l'animation et le message
    setThumbsDownAnimated(true);
    setThumbsDownMessage('Merci ! Bien reçu, nous revoyons le résultat !');
    
    // Arrêter l'animation après 1 seconde
    setTimeout(() => {
      setThumbsDownAnimated(false);
    }, 1000);
    
    // Masquer le message après 3 secondes
    setTimeout(() => {
      setThumbsDownMessage(null);
    }, 3000);
    
    // Envoyer un email : mode copié-collé → offre collée, mode lien web → URL seule
    const hasUrlForEmail = inputMode === 'url' && !!urlInput?.trim();
    const hasOfferForEmail = inputMode === 'text' && !!offreEmploi?.trim();
    if (score && (hasUrlForEmail || hasOfferForEmail)) {
      try {
        await sendThumbsDownEmail();
      } catch (error) {
        console.error('Erreur lors de l\'envoi de l\'email:', error);
        Sentry.captureException(error, { tags: { context: 'sendThumbsEmail' } });
      }
    }
  };

  // --- Junk food prize of the week ---
  const JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY = 'junkFoodPrizeVote';
  const JUNK_FOOD_PRIZE_CLIENT_ID_KEY = 'junkFoodPrizeClientId';

  const getClientId = () => {
    try {
      let id = localStorage.getItem(JUNK_FOOD_PRIZE_CLIENT_ID_KEY);
      if (!id) {
        id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
        localStorage.setItem(JUNK_FOOD_PRIZE_CLIENT_ID_KEY, id);
      }
      return id;
    } catch {
      return `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  };

  const getISOWeek = (date = new Date()) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
  };

  const simpleHash = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h) + str.charCodeAt(i);
      h = h & h;
    }
    return Math.abs(h).toString(36);
  };

  const getOfferId = () => {
    if (inputMode === 'url' && urlInput?.trim()) return urlInput.trim();
    const base = `${company || ''}|${position || ''}|${(explanation || '').slice(0, 500)}`;
    return `paste_${simpleHash(base)}`;
  };

  const getJunkFoodPrizeVoteStatus = () => {
    try {
      const raw = localStorage.getItem(JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const currentWeek = getISOWeek();
      if (data.week !== currentWeek) return null;
      return { week: data.week, offerId: data.offerId, company: data.company, position: data.position, offerUrl: data.offerUrl };
    } catch {
      return null;
    }
  };

  const handleVoteJunkFoodPrize = async () => {
    const offerId = getOfferId();
    if (!offerId) return;
    setIsVotingJunkFoodPrize(true);
    try {
      const db = getFirestore(app);
      const week = getISOWeek();
      const clientId = getClientId();
      const docId = `${week}_${clientId}`;
      const voteData = {
        offerId,
        offerUrl: inputMode === 'url' ? urlInput?.trim() || null : null,
        company: company || null,
        position: position || null,
        timestamp: serverTimestamp(),
        week,
      };
      await setDoc(doc(db, 'junkFoodPrizeVotes', docId), voteData, { merge: true });
      localStorage.setItem(JUNK_FOOD_PRIZE_VOTE_STORAGE_KEY, JSON.stringify({ week, offerId, company: company || null, position: position || null, offerUrl: inputMode === 'url' ? urlInput?.trim() || null : null }));
      logEvent(analytics, 'junk_food_prize_vote', { offer_id: offerId, is_change: hasVotedForOtherOffer, week });
    } catch (err) {
      console.error('Erreur vote Prix Malbouffe:', err);
      Sentry.captureException(err, { tags: { context: 'handleVoteJunkFoodPrize' } });
    } finally {
      setIsVotingJunkFoodPrize(false);
    }
  };

  const voteStatus = getJunkFoodPrizeVoteStatus();
  const currentOfferId = getOfferId();
  const hasVotedThisWeek = !!voteStatus;
  const hasVotedForOtherOffer = hasVotedThisWeek && voteStatus.offerId !== currentOfferId;
  const junkFoodPrizeVoteLabel = hasVotedForOtherOffer
    ? t('PrixMalbouffeChangeVote')
    : t('PrixMalbouffeVoteHere');

  // Fonction pour envoyer un email via Firestore Trigger Email
  const sendThumbsDownEmail = async () => {
    const db = getFirestore(app);
    const hasUrl = inputMode === 'url' && !!urlInput?.trim();
    const hasOffer = inputMode === 'text' && !!offreEmploi?.trim();
    const jobOfferToSend = hasOffer ? offreEmploi : '';

    const analysisResult = {
      score: score,
      scoreDetail: scoreDetail,
      position: position,
      company: company,
      explanation: explanation,
      improve: improve,
      debug: debugText,
      references: references,
      criteria: criteria,
      ...(hasOffer && { jobOffer: jobOfferToSend }),
      ...(hasUrl && { sourceUrl: urlInput.trim() }),
      model: selectedModel,
      timestamp: new Date().toISOString(),
      language: detectedLanguage
    };

    const urlSection = hasUrl
      ? `<p><strong>URL de la page:</strong> <a href="${urlInput.trim()}">${urlInput.trim()}</a></p>`
      : '';
    const offerSection = hasOffer
      ? `
            <h3>Offre d'emploi analysée:</h3>
            <div style="background-color: #f9f9f9; padding: 15px; border-radius: 5px; white-space: pre-wrap;">
${jobOfferToSend}
            </div>`
      : '';
    const urlText = hasUrl ? `URL de la page: ${urlInput.trim()}\n\n` : '';
    const offerText = hasOffer ? `\nOffre d'emploi analysée:\n${jobOfferToSend}` : '';

    try {
      await addDoc(collection(db, 'mail'), {
        to: 'me@jacquesgiraudel.com',
        message: {
          subject: `Thumbs Down - Analyse d'offre d'emploi - ${company || 'Entreprise inconnue'}`,
          html: `
            <h2>Thumbs Down - Analyse d'offre d'emploi</h2>
            ${urlSection}
            <p><strong>Entreprise:</strong> ${company || 'Non spécifiée'}</p>
            <p><strong>Poste:</strong> ${position || 'Non spécifié'}</p>
            <p><strong>Score Job-Nutriscore:</strong> ${score}/5</p>
            <p><strong>Modèle utilisé:</strong> ${selectedModel}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleString()}</p>
            
            <h3>Résultat complet de l'analyse (JSON):</h3>
            <pre style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; overflow-x: auto;">
${JSON.stringify(analysisResult, null, 2)}
            </pre>
            ${offerSection}
          `,
          text: `
Thumbs Down - Analyse d'offre d'emploi

${urlText}Entreprise: ${company || 'Non spécifiée'}
Poste: ${position || 'Non spécifié'}
Score Job-Nutriscore: ${score}/5
Modèle utilisé: ${selectedModel}
Date: ${new Date().toLocaleString()}

Résultat complet de l'analyse (JSON):
${JSON.stringify(analysisResult, null, 2)}${offerText}
          `
        }
      });
      
      console.log('Email de feedback envoyé avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de feedback:', error);
      Sentry.captureException(error, { tags: { context: 'sendFeedbackEmail' } });
      throw error;
    }
  };

  // Fonction pour extraire le contenu depuis une URL
  const extractFromUrl = async (url) => {
    try {
      const functions = getFunctions(app, 'europe-west3');
      const extractJobOfferFromWeb = httpsCallable(functions, 'extractJobOfferFromWeb');
      
      const requestBody = {
        url: url,
        model: selectedModel,
        saveToExtractedOffers: debug,
      };

      const result = await extractJobOfferFromWeb(requestBody);
      
      if (result.data && result.data.content) {
        return result.data.content;
      } else {
        throw new Error('Aucun contenu extrait de l\'URL');
      }
    } catch (error) {
      console.error('Erreur lors de l\'extraction depuis l\'URL:', error);
      if (error?.details?.reason !== 'SITE_NOT_SUPPORTED') {
        let domain = '';
        try {
          domain = new URL(url).hostname;
        } catch (_) { /* ignore */ }
        Sentry.captureException(error, {
          tags: {
            context: 'extractFromUrl',
            reason: error?.details?.reason || 'unknown',
            ...(domain && { domain }),
          },
          extra: { url },
        });
      }
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inputMode === 'url') setOffreEmploi('');
    else setUrlInput('');
    setIsLoading(true);
    setError(false);
    setExtractionError(null);
    setScore(null);
    setShowFinalScore(false);
    setCurrentLoadingScore(5);
    setLoadingAnimationRef(null);
    setShowReferences(false);
    
    // Réinitialiser les données de l'analyse précédente
    setCompany('');
    setPosition('');
    setSaveSuccess(false);
    setSaveError(false);
    setShowResponsibleApplication(false);
    setShouldBlinkResponsibleApplication(false);
    setShowOurBigTechs(false);
    setShowCalculationDetails(false);
    setCalculationDetails(null);
    setNotAProductJobOffer(false);

    // Scroll vers le bas pour voir l'animation
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);

    logEvent(analytics, 'submit_job_offer', {
      addToDatasetsDataSet: addToDatasetsDataSet,
      inputMode: inputMode
    });

    Sentry.addBreadcrumb({ category: 'analysis', message: 'Soumission analyse', data: { inputMode, model: selectedModel } });

    let jobOfferText;
    let data;

    const functions = getFunctions(app, 'europe-west3');

    // Mode URL : vérifier d'abord le cache par lien web
    if (inputMode === 'url') {
      if (!urlInput.trim()) {
        setError(true);
        setIsLoading(false);
        return;
      }
      try {
        const getJobOfferByUrl = httpsCallable(functions, 'getJobOfferByUrl', { timeout: 60000 });
        const cacheResp = await getJobOfferByUrl({ url: urlInput.trim() });
        if (cacheResp.data?.cached && typeof cacheResp.data.content === 'string') {
          data = { content: cacheResp.data.content };
        }
      } catch (cacheErr) {
        console.error('getJobOfferByUrl failed:', cacheErr);
        Sentry.captureException(cacheErr, { tags: { context: 'getJobOfferByUrl' }, extra: { url: urlInput?.trim?.() } });
        setError(true);
        setIsLoading(false);
        return;
      }
      if (!data) {
        try {
          jobOfferText = await extractFromUrl(urlInput.trim());
        } catch (err) {
          console.error('Erreur lors de l\'extraction depuis l\'URL:', err);
          if (err?.details?.reason !== 'SITE_NOT_SUPPORTED') {
            const url = urlInput?.trim?.();
            let domain = '';
            try {
              if (url) domain = new URL(url).hostname;
            } catch (_) { /* ignore */ }
            Sentry.captureException(err, {
              tags: {
                context: 'extractFromUrl',
                reason: err?.details?.reason || 'unknown',
                ...(domain && { domain }),
              },
              extra: { url },
            });
          }
          const isIndeed = err?.details?.reason === "SITE_NOT_SUPPORTED";
          setExtractionError(isIndeed ? t('ExtractionErrorIndeed') : (err?.message || t('ExtractionErrorGeneric')));
          setError(true);
          setIsLoading(false);
          return;
        }
      }
    } else {
      jobOfferText = document.getElementById('offreEmploi').value;
      if (!jobOfferText.trim()) {
        setError(true);
        setIsLoading(false);
        return;
      }
    }

    if (!data) {
      const uiLang = (i18n.language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en';
      const requestBody = {
        jobOffer: jobOfferText,
        outputLanguage: uiLang,
        debug: debug,
        addToDatasetsDataSet: addToDatasetsDataSet,
        model: selectedModel,
        debugBusinessInitiativeSpecialQuestion: debugBusinessInitiativeInput.trim() || undefined,
        debugExperimentalEnvironmentSpecialQuestion: debugExperimentalEnvironmentInput.trim() || undefined,
      };
      if (inputMode === 'url' && urlInput.trim()) {
        requestBody.sourceUrl = urlInput.trim();
      }
      try {
        const analyzeJobOffer = httpsCallable(functions, 'analyzeJobOffer', {
          timeout: 300000,
        });
        const callableResp = await analyzeJobOffer(requestBody);
        data = callableResp.data;
      } catch (cfErr) {
        console.error('API Error (callable failed):', cfErr);
        const errorKey = (cfErr && cfErr.details && cfErr.details.errorKey) || cfErr?.message;
        if (errorKey !== 'POSITION_NOT_PM_PO') {
          Sentry.captureException(cfErr, { tags: { context: 'analyzeJobOffer' }, extra: { inputMode, model: selectedModel } });
        }
        if (errorKey && typeof errorKey === 'string') {
          const translated = t(`Error_${errorKey}`);
          setExtractionError(translated !== `Error_${errorKey}` ? translated : null);
        } else {
          setExtractionError(null);
        }
        setError(true);
        setIsLoading(false);
        return;
      }
    }

    try {

      console.log(data);

      if (!data || typeof data.content !== 'string') {
        console.error('Unexpected response payload', data);
        Sentry.captureMessage('Réponse API inattendue', { level: 'error', extra: { dataKeys: data ? Object.keys(data) : [] } });
        setError(true);
        setIsLoading(false);
        return;
      }

      let result;
      try {
        result = JSON.parse(data.content.trim());
      } catch (e) {
        console.error('Failed to parse JSON from content', e, data.content);
        Sentry.captureException(e, { tags: { context: 'parseAnalysisResult' }, extra: { contentPreview: data?.content?.substring?.(0, 200) } });
        setError(true);
        setIsLoading(false);
        return;
      }

      if (! result.score) {
        setError(true);
        setIsLoading(false);
        return;
      }
      setError(false);
      fromShareRef.current = false;

      Sentry.addBreadcrumb({ category: 'analysis', message: 'Analyse réussie', data: { score: result.score, notAProductJobOffer: !!result.notAProductJobOffer } });

      // Vérifier si ce n'est pas une offre d'emploi produit ou pas une offre d'emploi du tout
      setNotAProductJobOffer(!!result.notAProductJobOffer);
      if (result.notAProductJobOffer === true) {
        setScore(1); // Forcer le score à E
        setExplanation("Le texte fourni ne semble pas être une offre d'emploi de Product Manager ou similaire. L'analyse Job-Nutriscore ne peut pas être appliquée à ce contenu.");
      } else {
        setScore(Math.round(result.score));
        setExplanation(result.explanation);
      }
      
      // Déclencher le clignotement si le score final est D ou E (≤ 2)
      const finalScore = result.notAProductJobOffer === true ? 1 : Math.round(result.score);
      if (finalScore <= 2) {
        setShouldBlinkResponsibleApplication(true);
        // Arrêter le clignotement après 10 secondes
        setTimeout(() => {
          setShouldBlinkResponsibleApplication(false);
        }, 10000);
      }
      
      // Déclencher l'animation finale après un court délai
      setTimeout(() => {
        setShowFinalScore(true);
      }, 100);
      try {
        const cleanReferences = DOMPurify.sanitize(result.references, {
          ADD_ATTR: ['target']
        }); 
        setReferences(cleanReferences); 
      } catch (error) {
        // TODO
      }
      if (result.calculationDetails) {
        setCalculationDetails(result.calculationDetails);
      }
      if (debug) {
        try {
          const cleanDebugText = DOMPurify.sanitize(result.debug); 
          setDebugText(cleanDebugText);

          const cleanImprove = DOMPurify.sanitize(result.improve); 
          setImprove(cleanImprove);          
          setScoreDetail(result.scoreDetail);
          setDebugBusinessInitiative(result.debugBusinessInitiative);
          setDebugBusinessInitiativeSpecialQuestion(result.debugBusinessInitiativeSpecialQuestion);
          setDebugExperimentalEnvironment(result.debugExperimentalEnvironment);
          setDebugExperimentalEnvironmentSpecialQuestion(result.debugExperimentalEnvironmentSpecialQuestion);
        } catch (error) {
          // TODO
        }        
      }
      setPosition(result.position);
      setCompany(result.company);

      // Conserver la langue de l'offre pour la sauvegarde/feedback (sans modifier l'interface)
      if (result.language) {
        setDetectedLanguage(result.language);
      }
      
      // Récupérer les criteria depuis la réponse de l'API
      if (result.criteria) {
        setCriteria(result.criteria);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors de la requête à l\'API ChatGPT:', error);
      Sentry.captureException(error, { tags: { context: 'handleSubmit' } });
      setIsLoading(false);
      // Gérer les erreurs de requête à l'API
    }
  };

  return (
    <div className="home-page">
      <div className="page-title">{t('PMOffer')}</div>
      <div className="page-subtitle">{t('PMOfferSubtitle')}</div>
      <form ref={formRef} onSubmit={handleSubmit} className="form-container">
        {/* Toggle entre les modes de saisie - lien web visible en développement ou lorsqu'un lien a été reçu en partage */}
        <div className="input-mode-toggle">
          <button
            type="button"
            className={`mode-button ${inputMode === 'url' ? 'active' : ''}`}
            onClick={() => setInputMode('url')}
          >
{t('URLModeButton')}
          </button>
          <button
            type="button"
            className={`mode-button ${inputMode === 'text' ? 'active' : ''}`}
            onClick={() => setInputMode('text')}
          >
{t('TextModeButton')}
          </button>
        </div>

        {inputMode === 'text' ? (
          <>
            <label htmlFor="offreEmploi" className="form-label">
              {t('JobOfferLabel')}
            </label>
            <textarea
              id="offreEmploi"
              value={offreEmploi}
              onChange={(e) => setOffreEmploi(e.target.value)}
              rows={20}
              className="text-area"
            />
          </>
        ) : (
          <>
            <label htmlFor="urlInput" className="form-label">
{t('URLInputLabel')}
            </label>
            <input
              id="urlInput"
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={t('URLInputPlaceholder')}
              className="url-input"
            />
            <p className="pwa-share-hint">
              {t('PwaShareHint')}
              {!isStandalone && (
                <>
                  {' '}
                  <button type="button" className="pwa-share-install-link" onClick={handleInstallClick}>
                    {t('PwaShareInstallLink')}
                  </button>
                  {showInstallInstructions && (
                    <span className="pwa-share-instructions">{t('PwaShareInstallInstructions')}</span>
                  )}
                </>
              )}
            </p>
          </>
        )}
        {isDevelopment && (
          <div className="debug-inputs">
            <label htmlFor="debugBusinessInitiative" className="form-label">
{t('DebugBusinessInitiativeLabel')}
            </label>
            <textarea
              id="debugBusinessInitiative"
              value={debugBusinessInitiativeInput}
              onChange={(e) => setDebugBusinessInitiativeInput(e.target.value)}
              rows={3}
              className="text-area debug-textarea"
              placeholder={t('DebugBusinessInitiativePlaceholder')}
            />
            
            <label htmlFor="debugExperimentalEnvironment" className="form-label">
{t('DebugExperimentalEnvironmentLabel')}
            </label>
            <textarea
              id="debugExperimentalEnvironment"
              value={debugExperimentalEnvironmentInput}
              onChange={(e) => setDebugExperimentalEnvironmentInput(e.target.value)}
              rows={3}
              className="text-area debug-textarea"
              placeholder={t('DebugExperimentalEnvironmentPlaceholder')}
            />
          </div>
        )}
        {isDevelopment && (
          <div className="dev-controls">
            <div className="model-selector">
              <label htmlFor="modelSelect" className="model-label">
{t('ModelSelectorLabel')}
              </label>
              <select
                id="modelSelect"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="model-select"
              >
                {availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkbox-container">
              <label htmlFor="addToDatasetsDataSet" className="checkbox-label">
                <input
                  type="checkbox"
                  id="addToDatasetsDataSet"
                  checked={addToDatasetsDataSet}
                  onChange={(e) => setAddToDatasetsDataSet(e.target.checked)}
                  className="checkbox-input"
                />
                <span className="checkbox-text">{t('AddToDatasetLabel')}</span>
              </label>
            </div>
          </div>
        )}
        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading ? (
            <div className="loading-content">
              <NutriScoreLoader />
              <span>{t('AnalysisInProgress')}</span>
            </div>
          ) : (
            t('SubmitButton')
          )}
        </button>
      </form>

      {score && (
          <ResultDisplay
            result={{
              score,
              explanation,
              references,
              position,
              company,
              calculationDetails,
              notAProductJobOffer,
            }}
            isLoading={isLoading}
            currentLoadingScore={currentLoadingScore}
            showFinalScore={showFinalScore}
            showCalculationDetails={showCalculationDetails}
            setShowCalculationDetails={setShowCalculationDetails}
            showReferences={showReferences}
            setShowReferences={setShowReferences}
            showResponsibleApplication={showResponsibleApplication}
            setShowResponsibleApplication={setShowResponsibleApplication}
            shouldBlinkResponsibleApplication={shouldBlinkResponsibleApplication}
            setShouldBlinkResponsibleApplication={setShouldBlinkResponsibleApplication}
            showOurBigTechs={showOurBigTechs}
            setShowOurBigTechs={setShowOurBigTechs}
            t={t}
            showThumbs
            onThumbsUp={handleThumbsUp}
            onThumbsDown={handleThumbsDown}
            thumbsUpAnimated={thumbsUpAnimated}
            thumbsDownAnimated={thumbsDownAnimated}
            thumbsUpMessage={thumbsUpMessage}
            thumbsDownMessage={thumbsDownMessage}
            banditManchotImageSrc="/images/bandit-manchot.png"
            onToggleSection={handleToggleSection}
            junkFoodPrizeVoteSection={
              !isLoading && score && (notAProductJobOffer ? 1 : score) <= 2 && inputMode === 'url' && !!urlInput?.trim() && (
                <div className="junk-food-prize-vote-row">
                  {voteStatus?.offerId === currentOfferId ? (
                    <p className="junk-food-prize-voted">
                      {t('PrixMalbouffeVotedThankYou')}
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://job-nutriscore.eu/${lang || 'fr'}/prix-malbouffe`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="junk-food-prize-linkedin-link"
                      >
                        {t('PrixMalbouffeShareOnLinkedIn')}
                      </a>
                      .
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="junk-food-prize-vote-btn"
                      onClick={handleVoteJunkFoodPrize}
                      disabled={isVotingJunkFoodPrize}
                    >
                      {isVotingJunkFoodPrize ? t('PrixMalbouffeVoting') : junkFoodPrizeVoteLabel}
                    </button>
                  )}
                </div>
              )
            }
          >
          {isDevelopment && !isLoading && score && (
                <div className="save-container">
                  <button 
                    onClick={handleSaveToFirebase} 
                    className="save-button" 
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <div className="loading-content">
                        <div className="spinner"></div>
                        <span>{t('Saving')}</span>
                      </div>
                    ) : (
t('SaveButton')
                    )}
                  </button>
                  {saveSuccess && (
                    <p className="save-success">{t('SaveSuccess')}</p>
                  )}
                  {saveError && (
                    <p className="save-error">{t('SaveError')}</p>
                  )}
                </div>
              )}
          {/* Section debug affichée seulement quand l'analyse est terminée */}
          {debug && !isLoading && score && (
                <>
                  <h5 className="debug-title">{t('ScoreDetailTitle')}</h5>
                  <div className="debug-text" dangerouslySetInnerHTML={{ __html: formatScoreDetail(scoreDetail) }}/>
                  <div className="debug-box">
                    <h5 className="debug-title">{t('DebugTitle')}</h5>
                    <div className="debug-text" dangerouslySetInnerHTML={{ __html: debugText }}/>
                  </div>
                  <div className="debug-box">
                    <h5 className="debug-title">{t('ImproveTitle')}</h5>
                    <div className="debug-text" dangerouslySetInnerHTML={{ __html: improve }}/>
                  </div>          
                  {debugBusinessInitiative && (
                    <div className="debug-box">
                      <h5 className="debug-title">{t('DebugBusinessInitiativeTitle')}</h5>
                      <div className="debug-text" dangerouslySetInnerHTML={{ __html: debugBusinessInitiative }}/>
                    </div>
                  )}
                  {debugBusinessInitiativeSpecialQuestion && (
                    <div className="debug-box">
                      <h5 className="debug-title">{t('DebugBusinessInitiativeSpecialTitle')}</h5>
                      <div className="debug-text" dangerouslySetInnerHTML={{ __html: debugBusinessInitiativeSpecialQuestion }}/>
                    </div>
                  )}
                  {debugExperimentalEnvironment && (
                    <div className="debug-box">
                      <h5 className="debug-title">{t('DebugExperimentalEnvironmentTitle')}</h5>
                      <div className="debug-text" dangerouslySetInnerHTML={{ __html: debugExperimentalEnvironment }}/>
                    </div>
                  )}
                  {debugExperimentalEnvironmentSpecialQuestion && (
                    <div className="debug-box">
                      <h5 className="debug-title">{t('DebugExperimentalEnvironmentSpecialTitle')}</h5>
                      <div className="debug-text" dangerouslySetInnerHTML={{ __html: debugExperimentalEnvironmentSpecialQuestion }}/>
                    </div>
                  )}
                </>
              )}
          </ResultDisplay>
      )}
    {error && (
      <p className="generic-error">{extractionError || (fromShareRef.current ? t('shareError') : t('genericError'))}</p>
    )}
    </div>
  );
}

function AnimatedNutriScoreVariant({ variant, title }) {
  const [index, setIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const cycle = [0, 1, 2, 3, 4];
    let letterIntervalMs = 700;
    let rotationStep = 6;
    let rotationTickMs = 30;

    if (variant === 2) {
      letterIntervalMs = 700;
      rotationStep = 72;
      rotationTickMs = 700;
    } else if (variant === 3) {
      letterIntervalMs = 600;
    } else if (variant === 4) {
      rotationStep = 10;
      rotationTickMs = 25;
    } else if (variant === 5) {
      rotationStep = 4;
      rotationTickMs = 40;
    } else if (variant === 6) {
      rotationStep = 8;
      rotationTickMs = 35;
    } else if (variant === 7) {
      letterIntervalMs = 1200;
      rotationTickMs = 40;
      rotationStep = 72 / (letterIntervalMs / rotationTickMs);
    }

    const letterTimer = setInterval(() => {
      setIndex((prev) => {
        const pos = cycle.indexOf(prev);
        const nextPos = (pos + 1) % cycle.length;
        return cycle[nextPos];
      });
      if (variant === 7) {
        // Balle qui grossit pour recouvrir le cercle, puis rétrécit (animation plus lente)
        setPulse(0);
        setTimeout(() => setPulse(0.3), 200);
        setTimeout(() => setPulse(1), 450);    // plein écran
        setTimeout(() => setPulse(1), 900);    // temps de pause visible
        setTimeout(() => setPulse(0), 1150);   // retour à la taille normale
      } else if (variant === 3) {
        setPulse(1);
        setTimeout(() => setPulse(0), 200);
      }
    }, letterIntervalMs);

    const rotationTimer = rotationTickMs
      ? setInterval(() => {
          setRotation((prev) => (prev + rotationStep) % 360);
        }, rotationTickMs)
      : null;

    return () => {
      clearInterval(letterTimer);
      if (rotationTimer) clearInterval(rotationTimer);
    };
  }, [variant]);

  const levels = [
    { color: '#028141', letter: 'A' },
    { color: '#83ba2b', letter: 'B' },
    { color: '#fdc705', letter: 'C' },
    { color: '#ef7c00', letter: 'D' },
    { color: '#e03d0b', letter: 'E' },
  ];
  const current = levels[index] || levels[0];
  const baseAngles = [-126, -54, 18, 90, 162];
  const innerBaseRadius = 48;
  const innerMaxRadius = 60;
  const clampedPulse = Math.max(0, Math.min(1, pulse));
  const innerRadius = innerBaseRadius + clampedPulse * (innerMaxRadius - innerBaseRadius);
  const baseFontSize = 40;
  const fontSize = baseFontSize + clampedPulse * 8;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
      <svg
        width="80"
        height="80"
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#038141"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[0] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#85bb2f"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[1] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#fecb01"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[2] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#ee8005"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[3] + rotation} 60 60)`}
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#e63e0f"
          strokeWidth="10"
          strokeDasharray="67.9 271.4"
          transform={`rotate(${baseAngles[4] + rotation} 60 60)`}
        />
        <circle cx="60" cy="60" r="49" fill="#ffffff" />
        <circle
          cx="60"
          cy="60"
          r={innerRadius}
          fill={current.color}
          style={{ transition: 'all 0.35s ease-out' }}
        />
        <text
          x="60"
          y="64"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          fontSize={fontSize}
          fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          fontWeight="700"
          style={{ transition: 'all 0.35s ease-out' }}
        >
          {current.letter}
        </text>
      </svg>
      <span>{title}</span>
    </div>
  );
}

// Composant page Admin
function AdminPage() {
  const [searchingListType, setSearchingListType] = useState(null);
  const [cac40Offers, setCac40Offers] = useState([]);
  const [cac40Error, setCac40Error] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);

  const handleAnalyzeAll = async () => {
    if (cac40Offers.length === 0) return;
    setAnalyzingAll(true);
    setAnalysisResults([]);
    const functions = getFunctions(app, 'europe-west3');
    const extractJobOfferFromWeb = httpsCallable(functions, 'extractJobOfferFromWeb');
    const analyzeJobOffer = httpsCallable(functions, 'analyzeJobOffer', { timeout: 300000 });
    const uiLang = (i18n.language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en';
    const model = 'gpt-4.1-2025-04-14';

    const getJobOfferByUrl = httpsCallable(functions, 'getJobOfferByUrl', { timeout: 60000 });

    const analyzeOne = async (offer) => {
      try {
        const cacheResp = await getJobOfferByUrl({ url: offer.url });
        let data = null;
        if (cacheResp?.data?.cached && typeof cacheResp?.data?.content === 'string') {
          data = { content: cacheResp.data.content };
        }
        if (!data) {
          const extractResp = await extractJobOfferFromWeb({ url: offer.url, model });
          const jobOfferText = extractResp?.data?.content;
          if (!jobOfferText) throw new Error('Aucun contenu extrait');
          const analyzeResp = await analyzeJobOffer({
            jobOffer: jobOfferText,
            outputLanguage: uiLang,
            sourceUrl: offer.url,
            model,
          });
          data = analyzeResp?.data;
        }
        if (!data?.content) throw new Error('Réponse invalide');
        const result = JSON.parse(data.content.trim());
        return {
          ...offer,
          score: result.notAProductJobOffer ? 1 : Math.round(result.score),
          explanation: result.explanation || '',
          error: null,
        };
      } catch (err) {
        return { ...offer, score: null, explanation: null, error: err?.message || 'Erreur' };
      }
    };

    const results = await Promise.all(cac40Offers.map(analyzeOne));
    setAnalysisResults(results);
    setAnalyzingAll(false);
  };

  const handleSearch = async (listType) => {
    setSearchingListType(listType);
    setCac40Error(null);
    setCac40Offers([]);
    setAnalysisResults([]);
    try {
      const functions = getFunctions(app, 'europe-west3');
      const searchCAC40JobOffers = httpsCallable(functions, 'searchCAC40JobOffers', { timeout: 300000 });
      const result = await searchCAC40JobOffers({ listType });
      const data = result?.data || {};
      setCac40Offers(data.offers || []);
      if (data.offers?.length === 0 && !data.message) {
        setCac40Error('Aucune offre trouvée.');
      }
    } catch (err) {
      setCac40Error(err?.message || 'Erreur lors de la recherche.');
      setCac40Offers([]);
    } finally {
      setSearchingListType(null);
    }
  };

  return (
    <div className="admin-page">
      <h2>Administration</h2>
      <section className="admin-section">
        <h3>Animation Job-Nutriscore (barillet + pulse)</h3>
        <AnimatedNutriScoreVariant variant={7} title="Variante choisie – barillet + pulse central" />
      </section>
      <section className="admin-section">
        <h3>Offres Product Manager / Product Owner</h3>
        <p>Recherche via OpenAI (web search) les offres PM/PO pour les entreprises (normalizedCompanies), vérifie que chaque lien est toujours valide, puis affiche les résultats.</p>
        <div className="admin-scan-buttons">
          <button
            type="button"
            className="admin-cac40-btn"
            onClick={() => handleSearch('cac40')}
            disabled={!!searchingListType}
          >
            {searchingListType === 'cac40' ? 'Recherche en cours…' : 'CAC40'}
          </button>
          <button
            type="button"
            className="admin-cac40-btn"
            onClick={() => handleSearch('frenchtech')}
            disabled={!!searchingListType}
          >
            {searchingListType === 'frenchtech' ? 'Recherche en cours…' : 'French Tech'}
          </button>
        </div>
        {cac40Error && <p className="generic-error">{cac40Error}</p>}
        {cac40Offers.length > 0 && (
          <div className="admin-cac40-offers">
            <h4>{cac40Offers.length} offre(s) trouvée(s)</h4>
            <button
              type="button"
              className="admin-cac40-btn"
              onClick={handleAnalyzeAll}
              disabled={analyzingAll}
            >
              {analyzingAll ? 'Analyse en cours…' : 'Analyser toutes les offres'}
            </button>
            {analysisResults.length > 0 && (
              <div className="admin-analysis-results">
                <h4>Résultats d'analyse</h4>
                <table className="admin-analysis-table">
                  <thead>
                    <tr>
                      <th>Entreprise</th>
                      <th>Titre</th>
                      <th>Score</th>
                      <th>Lien</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisResults.map((r, i) => (
                      <tr key={i}>
                        <td>{r.company || '–'}</td>
                        <td>{r.title || '–'}</td>
                        <td>
                          {r.error ? (
                            <span className="admin-analysis-error">{r.error}</span>
                          ) : (
                            <span className={`admin-score-${r.score}`}>
                              {['E','D','C','B','A'][r.score - 1] || r.score}
                            </span>
                          )}
                        </td>
                        <td>
                          <a href={r.url} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ul className="admin-cac40-list">
              {cac40Offers.map((o, i) => (
                <li key={i} className="admin-cac40-item">
                  <a href={o.url} target="_blank" rel="noopener noreferrer">
                    {(o.company ? `${o.company} – ` : '')}{o.title || o.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

// Composant de navigation
function Navigation({ isDevelopment }) {
  const location = useLocation();
  const { path } = useLang();
  const { t } = useTranslation();

  if (!isDevelopment) return null;

  return (
    <nav className="navigation">
      <ul className="nav-list">
        <li className={/^\/[a-z]{2}\/?$/.test(location.pathname) ? 'nav-item active' : 'nav-item'}>
          <Link to={path('/')} className="nav-link">{t('NavigationHome')}</Link>
        </li>
        <li className={location.pathname.endsWith('/offres') ? 'nav-item active' : 'nav-item'}>
          <Link to={path('/offres')} className="nav-link">{t('NavigationOffers')}</Link>
        </li>
        <li className={location.pathname.endsWith('/evals-management') ? 'nav-item active' : 'nav-item'}>
          <Link to={path('/evals-management')} className="nav-link">{t('NavigationEvals')}</Link>
        </li>
        <li className={location.pathname.endsWith('/evals') ? 'nav-item active' : 'nav-item'}>
          <Link to={path('/evals')} className="nav-link">{t('NavigationFineTuning')}</Link>
        </li>
        <li className={location.pathname.endsWith('/admin') ? 'nav-item active' : 'nav-item'}>
          <Link to={path('/admin')} className="nav-link">{t('NavigationAdmin')}</Link>
        </li>
      </ul>
    </nav>
  );
}

function App() {
  const { t } = useTranslation();
  
  // Vérifier si l'app est en mode développement
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // État pour l'authentification
  const [email, setEmail] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true); // Changé à true par défaut
  const [toaster, setToaster] = useState({ message: '', type: 'info', isVisible: false });

  // Fonction pour afficher un toaster
  const showToaster = (message, type = 'info') => {
    setToaster({ message, type, isVisible: true });
  };

  // Fonction pour fermer le toaster
  const closeToaster = () => {
    setToaster(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    let bundle = {
      screen_name: "home_page",
      screen_class: "HomePage",
    };

    logEvent(analytics, 'screen_view', bundle);
    
    // Écouter les changements d'état d'authentification
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Utilisateur connecté
        setIsAuthenticated(true);
        setEmail(user.email || '');
        setAuthLoading(false);
      } else {
        // Utilisateur déconnecté
        setIsAuthenticated(false);
        setEmail('');
        setAuthLoading(false);
      }
    });

    // Vérifier si l'utilisateur arrive via un lien de connexion
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Récupérer l'email depuis le localStorage
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        // Si l'email n'est pas dans le localStorage, demander à l'utilisateur
        email = window.prompt(t('AuthEmailPrompt'));
      }
      
      if (email) {
        setAuthLoading(true);
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            window.localStorage.removeItem('emailForSignIn');
            // L'état sera mis à jour automatiquement par onAuthStateChanged
            showToaster('Connexion réussie !', 'success');
          })
          .catch((error) => {
            console.error('Erreur lors de la connexion:', error);
            Sentry.captureException(error, { tags: { context: 'auth_signInWithEmailLink' } });
            showToaster('Erreur lors de la connexion: ' + error.message, 'error');
            setAuthLoading(false);
          });
      }
    }

    // Nettoyer l'écouteur lors du démontage du composant
    return () => unsubscribe();
  }, []);

  // Fonction pour envoyer le lien de connexion
  const handleSendSignInLink = async () => {
    if (!email) {
      showToaster('Veuillez saisir une adresse email', 'warning');
      return;
    }

    setAuthLoading(true);

    try {
      const auth = getAuth(app);
      const actionCodeSettings = {
        url: window.location.href,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Sauvegarder l'email dans le localStorage
      window.localStorage.setItem('emailForSignIn', email);
      
      showToaster(t('AuthLinkSentMessage'), 'success');
      setAuthLoading(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du lien:', error);
      Sentry.captureException(error, { tags: { context: 'auth_sendSignInLink' } });
      showToaster('Erreur lors de l\'envoi du lien: ' + error.message, 'error');
      setAuthLoading(false);
    }
  };

  // Fonction pour se déconnecter
  const handleSignOut = async () => {
    try {
      const auth = getAuth(app);
      await auth.signOut();
      setIsAuthenticated(false);
      setEmail('');
      showToaster('Déconnexion réussie', 'success');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      Sentry.captureException(error, { tags: { context: 'auth_signOut' } });
      showToaster('Erreur lors de la déconnexion: ' + error.message, 'error');
    }
  };

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route
        path="/:lang/*"
        element={
          <LangLayout
            isDevelopment={isDevelopment}
            isAuthenticated={isAuthenticated}
            authLoading={authLoading}
            email={email}
            setEmail={setEmail}
            handleSendSignInLink={handleSendSignInLink}
            handleSignOut={handleSignOut}
            toaster={toaster}
            closeToaster={closeToaster}
            t={t}
          />
        }
      >
        <Route index element={<HomePage isAuthenticated={isAuthenticated} isDevelopment={isDevelopment} />} />
        <Route path="classement-entreprises" element={<ClassementEntreprises />} />
        <Route path="prix-malbouffe" element={<PrixMalbouffe />} />
        {isDevelopment && (
          <>
            <Route path="offres" element={<Offres />} />
            <Route path="evals" element={<FineTuning />} />
            <Route path="evals-management" element={<Evals />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="projections-france" element={<ProjectionsFrance />} />
          </>
        )}
      </Route>
    </Routes>
  );
 }

export default App;

export function browserLocales(languageCodeOnly = false) {
  return navigator.languages.map((locale) =>
    languageCodeOnly ? locale.split("-")[0] : locale,
  );
}